import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';

const ScrollReveal = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => el.classList.add('revealed'), delay);
          obs.unobserve(el);
        }
      },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay]);
  return <div ref={ref} className="scroll-reveal">{children}</div>;
};

export default function Contact() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ nom: '', prenom: '', email: '', event_name: '', participants: '', pack_choice: '', event_date: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const packOptions = t('home.contact.pack_options', { returnObjects: true });

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitStatus(null);
    try {
      const res = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Send failed');
      setSubmitStatus('success');
      setForm({ nom: '', prenom: '', email: '', event_name: '', participants: '', pack_choice: '', event_date: '', message: '' });
    } catch {
      setSubmitStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = 'w-full bg-white/5 border border-white/20 text-white placeholder-white/50 rounded-sm px-4 py-3 text-base font-manrope focus:outline-none focus:border-[#F5820D] transition-colors';
  const labelClass = 'block text-white text-sm font-oswald font-bold uppercase tracking-wider mb-1.5';

  return (
    <div className="min-h-screen bg-[#111111]">
      <section
        className="relative min-h-screen py-12 md:py-32"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/3621185/pexels-photo-3621185.jpeg?auto=compress&cs=tinysrgb&w=1600)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
        }}
      >
        <div className="absolute inset-0" style={{ backgroundColor: 'rgba(17,17,17,0.43)' }} />
        <div className="relative z-10 max-w-4xl mx-auto px-6">
            <ScrollReveal>
              <div className="text-center mb-6 md:mb-12">
                <div className="flex items-center justify-center gap-4 mb-4">
                  <div className="w-1 h-10 bg-[#F5820D]" />
                  <h1 className="font-oswald text-3xl md:text-5xl font-black uppercase text-white">
                    {t('home.contact.title')}
                  </h1>
                  <div className="w-1 h-10 bg-[#F5820D]" />
                </div>
                <p className="font-manrope text-white text-lg font-medium">{t('home.contact.subtitle')}</p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={100}>
              <form onSubmit={handleSubmit} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-sm p-4 md:p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-5 mb-3 md:mb-5">
                  <div>
                    <label className={labelClass}>{t('home.contact.nom')}</label>
                    <input name="nom" value={form.nom} onChange={handleChange} required placeholder="Dupont" className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>{t('home.contact.prenom')}</label>
                    <input name="prenom" value={form.prenom} onChange={handleChange} required placeholder="Jean" className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>{t('home.contact.email')}</label>
                    <input name="email" type="email" value={form.email} onChange={handleChange} required placeholder="votre@email.com" className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>{t('home.contact.event_name')}</label>
                    <input name="event_name" value={form.event_name} onChange={handleChange} required placeholder="Trail des Ardennes 2026" className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>{t('home.contact.participants')}</label>
                    <input name="participants" value={form.participants} onChange={handleChange} placeholder="500" className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>{t('home.contact.pack_choice')}</label>
                    <select name="pack_choice" value={form.pack_choice} onChange={handleChange} className={`${inputClass} cursor-pointer`}>
                      <option value="" className="bg-[#111]">—</option>
                      {Array.isArray(packOptions) && packOptions.map((opt, i) => (
                        <option key={i} value={opt} className="bg-[#111]">{opt}</option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelClass}>{t('home.contact.event_date')}</label>
                    <input name="event_date" type="date" value={form.event_date} onChange={handleChange} className={inputClass} />
                  </div>
                </div>
                <div className="mb-4 md:mb-6">
                  <label className={labelClass}>{t('home.contact.message')}</label>
                  <textarea name="message" value={form.message} onChange={handleChange} rows={4} placeholder="Décrivez votre événement et vos besoins..." className={`${inputClass} resize-none`} />
                </div>

                {submitStatus === 'success' && (
                  <div className="flex items-center gap-3 bg-green-900/40 border border-green-500/30 text-green-400 text-sm font-manrope px-4 py-3 rounded-sm mb-4">
                    <CheckCircle size={16} />
                    {t('home.contact.success')}
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div className="flex items-center gap-3 bg-red-900/40 border border-red-500/30 text-red-400 text-sm font-manrope px-4 py-3 rounded-sm mb-4">
                    <AlertCircle size={16} />
                    {t('home.contact.error')}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#F5820D] hover:bg-[#D9730B] disabled:opacity-60 text-white font-oswald uppercase tracking-widest text-sm py-4 flex items-center justify-center gap-2 transition-all duration-300 hover:-translate-y-0.5"
                >
                  {submitting ? <><Loader2 size={16} className="animate-spin" /> {t('home.contact.sending')}</> : t('home.contact.submit')}
                </button>
              </form>
            </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
